import { Enum_Translation_Locale, formatDate, formatDTsToReportDateFormat, isEnglishWithSpecialChars, notNull, NoYearDateTimeFormat, parseDateV2, SystemDateTimeSec, useUpdatePatientVisitUpdateAdhocCommentMutation } from '@cyren/common-lib';
import classNames from 'classnames';
import React, { HTMLAttributes, memo, useEffect, useRef, useState } from 'react';
import { BiPencil } from 'react-icons/bi';
import { FaShareSquare, FaArrowCircleDown, FaPaperPlane } from 'react-icons/fa';
import { IoMdCopy } from 'react-icons/io';
import { useCopyToClipboard } from 'usehooks-ts';
import { H5 } from '../../common/typo/H';
import { FlowLineConfigT, PHITranslated } from '../../survey/template/template-types';
import { consolidateFlowItem } from '../../survey/template/utils/flow-template';
import { getStringFromLine, getStringFromPhis } from '../../utils/models/template';
import { useElectron } from '../hooks/use-electron';
import { useToastMsgs } from '../hooks/use-toast-msgs';
import useAuth from '../../auth/use-auth';
import { Accordion, AccordionDetails, AccordionSummary, Switch, FormControlLabel } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { isEmpty, isNil, union, uniqWith } from 'lodash';
import { PaTreePure } from '../../patient/tree/PaTreePage';
import { TPatientVisit, TTransApi } from '../../graphql-enhanced';
import { AdhocCommentType, ReportStateType } from '../../patient/patient-types'; // ReportStateType 임포트 추가
import FaProCommentMedical from '../../components/FaPro/FaProCommentMedical';
import ModalAddComment from './components/ModalAddComment';
import { genMessageLongId } from '../nav-utils';
import { onError } from '../../utils/apollo-utils';
import { GrMoreVertical } from 'react-icons/gr';
import useConfirmationDialog from '../../hooks/use-confirmation-dialog';
import GenerateDescriptiveNoteButton from './components/DescriptiveButton'; // DescriptiveButton 임포트
import { useLocalization } from '../../report/use-localization';
import { useUserTranslateStrings } from '../../report/use-user-translate-strings';
import { useStaticLocales } from '../hooks/locales/use-static-locale';
import { useRecoilState } from 'recoil';
import { PaDataRepoState, useData } from '../../report/use-data';

const html2pdf = require('html2pdf.js/dist/html2pdf.bundle.min');

function convertLabelstoJSXInternal(
  input: string,
  td?: (key?: string) => string | undefined | null,
  tNote?: (key?: string) => string | undefined | null,
  locale?: string,
  getLanguageName?: (localeCode: string) => string,
  autoTranslatedLabel?: string,
  replaceMap?: Record<string, string>,
) {
  
  const regex = /<([^>]+)>([\s\S]*?)<\/\1>/g;
  const parts = [];

  let lastIndex = 0;

  let tempInput = input;

  // [FIX] Remove JSON artifacts like ["o-day"]["1"] and format symptom keys
  if (tempInput) {
    tempInput = tempInput.replace(/\["([^"]*)"\]/g, (match, content) => {
      if (!content) return "";
      
      // Try translation first if td is available
      if (td) {
          const translated = td(content);
          if (translated && translated !== content) return translated;
      }

      // If it starts with known prefixes, format it
      if (content.startsWith('sx-') || content.startsWith('o-')) {
          const formatted = content.replace(/^(sx-|o-)/, '').replace(/-/g, ' ');
          return formatted.charAt(0).toUpperCase() + formatted.slice(1);
      }
      
      // If purely numeric, likely an artifact/value without context -> hide it
      if (/^\d+$/.test(content)) {
          return "";
      }

      // Default: return content without brackets/quotes
      return content;
    });
  }

  tempInput = tempInput.replaceAll("NRS <cyrenvalue>", "<cyrenvalue>NRS ");
  tempInput = tempInput.replaceAll(": ", " ");
  // cyrenvalue 태그 내부의 \n은 보존하고, 태그 외부의 \n만 공백으로 변경
  // multi_text_nested의 그룹화된 출력을 위해 cyrenvalue 태그 내부의 \n은 그대로 유지
  // 정규식을 더 정확하게 수정하여 cyrenvalue 태그 내부의 \n을 보존
  tempInput = tempInput.replace(/\n(?![^<]*<\/cyrenvalue>)/g, " ");
  

  let match = regex.exec(tempInput);
  let firstValue = true;
  while (match !== null) {
    const [fullMatch, tagName, innerText] = match;
    let progressedInnerText = innerText;
    const combinedSymptoms =
      replaceMap?.['Combined Sx'] ?? 'Combined Symptoms';
    const symptomProgression =
      replaceMap?.['Sx progression'] ?? 'Symptom Progression';
    progressedInnerText = progressedInnerText.replace('Combined Sx', combinedSymptoms);
    progressedInnerText = progressedInnerText.replace('Sx progression', symptomProgression);
    const { index } = match;

    let isLineStart = true;
    if (index > lastIndex) {
      parts.push(tempInput.substring(lastIndex, index));
      isLineStart = false;
    }

    let tags = "";
    if (tagName.toLowerCase() === "cyrenvalue") {
      tags = classNames("text-black", (firstValue && !isLineStart) && "ml-1");
    }
    if (tagName.toLowerCase() === "cyrenheader") {
      tags = "bg-gray-100 font-bold rounded-md text-black p-1";
    }
    // cyrenlabel 처리: 빈 라벨은 건너뛰기
    const isCyrenLabel = tagName.toLowerCase() === "cyrenlabel";
    const trimmedText = progressedInnerText?.trim() || '';
    const isEmptyLabel = isCyrenLabel && (
      !progressedInnerText || 
      !trimmedText || 
      trimmedText === ':' || 
      trimmedText === '()' ||
      trimmedText === '():' ||
      trimmedText === '( ):' ||
      trimmedText === '(' ||
      trimmedText === ')'
    );
    
    if (isEmptyLabel) {
      // 빈 라벨 태그는 건너뛰고 다음 매치로 이동
      lastIndex = index + fullMatch.length;
      match = regex.exec(tempInput);
      firstValue = false;
    } else if (isCyrenLabel) {
      // Report template labels must stay literal (no runtime translation).
      tags = "text-gray-500";

      parts.push(
        <span key={index} className={tags}>
          {progressedInnerText || innerText}
        </span>
      );
      firstValue = false;
      lastIndex = index + fullMatch.length;
      match = regex.exec(tempInput);
    } else {
      // cyrenlabel이 아닌 경우 처리
      if (tagName.toLowerCase() === "strong") {
        tags = "font-bold";
      }
      
      // cyrenvalue 태그 내부의 텍스트는 tNote로 번역 (주관식 답변)
      // cyrenvalue가 아닌 경우는 td로 번역 (의료 용어)
      let translatedText = progressedInnerText;
      let isTranslated = false;
      
      if (tagName.toLowerCase() === "cyrenvalue") {
        // cyrenvalue 태그 내부는 tNote로 번역 (주관식 답변)
        // 괄호 안의 내용만 번역 (예: "Other (何かずっと疲れた感じがして、元気がありません。)" -> "Other (I've been feeling tired...)")
        if (tNote) {
          // 괄호 안의 내용을 찾아서 번역
          const parenthesesMatch = progressedInnerText.match(/\(([^)]+)\)/);
          if (parenthesesMatch) {
            const noteInParentheses = parenthesesMatch[1];
            const translatedNote = tNote(noteInParentheses);
            
            if (translatedNote && translatedNote !== noteInParentheses) {
              // 괄호 안의 내용을 번역된 내용으로 교체
              translatedText = progressedInnerText.replace(
                `(${noteInParentheses})`,
                `(${translatedNote})`
              );
              isTranslated = true;
            } else {
              // 번역이 없으면 원본 유지
              translatedText = progressedInnerText;
              isTranslated = false;
            }
          } else {
            // 괄호가 없으면 전체 텍스트를 번역 시도
            translatedText = tNote(progressedInnerText) || progressedInnerText;
            isTranslated = !!(translatedText && translatedText !== progressedInnerText);
          }
        } else {
          translatedText = progressedInnerText;
          isTranslated = false;
        }
      } else {
        // cyrenvalue가 아닌 경우는 td로 번역 (의료 용어)
        const rawText = progressedInnerText;
        let textToDisplay = td ? (td(rawText) || rawText) : rawText;
        
        // 번역 실패 시, sx-나 o-로 시작하면 포맷팅 시도
        if (textToDisplay === rawText && (rawText.startsWith('sx-') || rawText.startsWith('o-'))) {
            textToDisplay = rawText.replace(/^(sx-|o-)/, '').replace(/-/g, ' ');
            // 첫 글자 대문자화
            textToDisplay = textToDisplay.charAt(0).toUpperCase() + textToDisplay.slice(1);
        }

        translatedText = textToDisplay;
        isTranslated = !!(translatedText && translatedText !== rawText);
      }
      
      // <br/> 태그를 실제 줄바꿈으로 변환하고 <strong> 태그를 처리
      const processTextWithBreaks = (text: string) => {
        return text.split('<br/>').map((part, idx) => (
          <React.Fragment key={idx}>
            {processStrongTags(part)}
            {idx < text.split('<br/>').length - 1 && <br />}
          </React.Fragment>
        ));
      };
      
      // <strong> 태그를 처리하는 함수
      const processStrongTags = (text: string) => {
        const strongRegex = /<strong>(.*?)<\/strong>/g;
        const strongParts = [];
        let strongLastIndex = 0;
        let strongMatch = strongRegex.exec(text);
        
        while (strongMatch !== null) {
          // strong 태그 이전의 텍스트 추가
          if (strongMatch.index > strongLastIndex) {
            strongParts.push(text.substring(strongLastIndex, strongMatch.index));
          }
          
          // strong 태그 내부의 텍스트를 bold로 처리
          strongParts.push(<strong key={strongMatch.index}>{strongMatch[1]}</strong>);
          
          strongLastIndex = strongMatch.index + strongMatch[0].length;
          strongMatch = strongRegex.exec(text);
        }
        
        // 마지막 부분 추가
        if (strongLastIndex < text.length) {
          strongParts.push(text.substring(strongLastIndex));
        }
        
        return strongParts.length > 0 ? strongParts : text;
      };
      
      parts.push(
        <span key={index} className={tags}>
          {processTextWithBreaks(translatedText || innerText)}
          {isTranslated && (() => {
            // 괄호 안의 원본 텍스트 추출 (번역 표시용)
            const parenthesesMatch = progressedInnerText.match(/\(([^)]+)\)/);
            const originalNote = parenthesesMatch ? parenthesesMatch[1] : progressedInnerText;
            const languageName = getLanguageName && locale ? getLanguageName(locale) : '';
            const autoTranslatedText = autoTranslatedLabel ?? 'Auto-translated from';
            return (
              <div className="text-xs text-gray-400 mt-1">
                [{autoTranslatedText} {languageName} <span className="italic">{originalNote}</span>]
              </div>
            );
          })()}
        </span>
      );
      firstValue = false;

      lastIndex = index + fullMatch.length;

      match = regex.exec(tempInput);
    }
  }

  if (lastIndex < tempInput.length) {
    parts.push(tempInput.substring(lastIndex));
  }

  return <span className="whitespace-pre-wrap">{parts}</span>;
}

export function convertLabelstoJSX(
  input: string | null | undefined | string[],
  td?: (key?: string) => string | undefined | null,
  tNote?: (key?: string) => string | undefined | null,
  locale?: string,
  getLanguageName?: (localeCode: string) => string,
  autoTranslatedLabel?: string,
  replaceMap?: Record<string, string>,
) {
  if (!input) {
    return null;
  }

  if (Array.isArray(input)) {
    return input.map((i) => {
      return convertLabelstoJSXInternal(
        i,
        td,
        tNote,
        locale,
        getLanguageName,
        autoTranslatedLabel,
        replaceMap,
      );
    });
  }

  return convertLabelstoJSXInternal(
    input,
    td,
    tNote,
    locale,
    getLanguageName,
    autoTranslatedLabel,
    replaceMap,
  );
}


function ReportCard({
  line,
  phis,
  demoSignature,
  keyId,
  isAddOnForm,
  adHocData,
  visit,
  markUserActive,
  // ReportPage로부터 전달받을 새로운 props 추가
  reportState,
  onReportGenerated,
  setIsGeneratingDesc,
  isDemo,
  td,
  renderTranslatedText,
}: HTMLAttributes<HTMLDivElement> & {
  line: FlowLineConfigT;
  phis?: PHITranslated[];
  demoSignature?: string | null;
  keyId: string;
  isAddOnForm: boolean;
  adHocData?: {
    adHocComments: AdhocCommentType[] | null;
  } | null,
  visit: TPatientVisit | null;
  markUserActive?: () => void;
  // 새로운 props 타입 정의
  reportState: ReportStateType; // ReportStateType 임포트 필요
  onReportGenerated: (generatedText: string | null) => void;
  setIsGeneratingDesc: (isLoading: boolean) => void;
  isDemo: boolean | false;
  td?: (key?: string) => string | undefined | null;
  renderTranslatedText?: (text: string) => string | React.ReactElement;
}) {
  const [, copy] = useCopyToClipboard();
  const { isElectron } = useElectron();
  const { showNothingToCopyMsg, showCopiedMsg } = useToastMsgs();
  const [isSendingData, setIsSendingData] = useState(false);

  // 번역 기능 추가
  const [{ appStateCookie }, { trans }] = useLocalization();
  const { sltStr } = useStaticLocales();

  // userNoteTrans를 가져와서 tNote 함수 생성
  const [dataState] = useRecoilState(PaDataRepoState);
  const { userNoteTrans } = dataState;
  const locale = reportState?.locale || "english";
  const reportAutoTranslatedLabel = sltStr({ key: 'reports-auto-translated-from' });
  const reportReplaceMap = {
    'Combined Sx': sltStr({ key: 'report-combined-symptoms' }),
    'Sx progression': sltStr({ key: 'report-symptom-progression' }),
  };
  const renderReportText = (input: string | null | undefined | string[]) =>
    convertLabelstoJSX(
      input,
      td,
      tNote,
      locale,
      getLanguageName,
      reportAutoTranslatedLabel,
      reportReplaceMap,
    );

  // 질문 정보를 가져오기 위해 useData 사용
  const [, { getQuestionByKey }] = useData();

  // locale 코드를 언어 이름으로 변환하는 함수
  const getLanguageName = (localeCode: string): string => {
    const languageMap: { [key: string]: string } = {
      'english': 'English',
      'japanese': 'Japanese',
      'korean': 'Korean',
      'spanish': 'Spanish',
      'chinese': 'Chinese',
      'french': 'French',
      'german': 'German',
      'italian': 'Italian',
      'portuguese': 'Portuguese',
      'russian': 'Russian',
      'arabic': 'Arabic',
      'hindi': 'Hindi',
      'thai': 'Thai',
      'vietnamese': 'Vietnamese',
    };
    return languageMap[localeCode.toLowerCase()] || localeCode.charAt(0).toUpperCase() + localeCode.slice(1);
  };

  // tNote 함수: userNoteTrans에서 주관식 답변 번역 찾기
  const tNote = (note?: string | null) => {
    if (!note) return note;
    const noteTran = userNoteTrans?.find((tra) =>
      tra.target?.trim() === note.trim() &&
      locale === tra.target_locale
    );
    return noteTran?.output || note;
  };

  const [{ user }] = useAuth();
  const [openModalAddComment, setOpenModalAddComment] = useState(false);
  const [editComment, setEditComment] = useState<AdhocCommentType | null>(null);
  const [confirmDialog, confirmationDialogUX] = useConfirmationDialog();

  const [updateAdhocComment] = useUpdatePatientVisitUpdateAdhocCommentMutation({
    onError,
  });

  const [{ jobFunction }] = useAuth();

  const downloadableForm = useRef(null);
  const treeKey = line.items[0].reportConfig?.treeKey;

  const isPhiLine = phis && line.items[0].type === 'rep-phi-list';
  const isHpiLine = line.label === "HPI";
  const [showDescriptiveReport, setShowDescriptiveReport] = useState(false);

  const promptInfoHpi: any[] = [];
  const promptInfoPmh: any[] = [];

  phis?.forEach((phi) => {
    const cleanedOutput = phi.lineOutput.replace(/<cyrenvalue>/g, "").replace(/<\/cyrenvalue>/g, "");
    promptInfoHpi.push(`${phi.treeKey}: ${cleanedOutput} Onset Date: ${phi.onsetDate} Relative Onset: ${phi.relativeOnset ?? ""}`);
    if (phi.sublinesOutput.length > 0) {
      phi.sublinesOutput.forEach((subline) => {
        promptInfoHpi.push(`${phi.treeKey}: ${subline} Onset Date: ${phi.onsetDate} Relative Onset: ${phi.relativeOnset ?? ""}`);
      });
    }
  });

  const [chiefComplaintsTranslations, setChiefComplaintsTranslations] = useState<TTransApi[]>([]);
  const [translateUserStrings] = useUserTranslateStrings((results: TTransApi[]) => {
    // add any newly received translated strings into the map, but keep the old ones as well
    setChiefComplaintsTranslations(union(chiefComplaintsTranslations, results));
  });

  // CC 번역 요청 로직 추가
  useEffect(() => {
    if (!isDemo && line.label === "CC" && reportState?.systemValueMap?.cc) {
      const cc = reportState.systemValueMap.cc as string;
      
      // 영어가 아니거나 특수문자가 포함된 경우 번역 요청
      if (locale !== "english" || !isEnglishWithSpecialChars(cc)) {
        // 이미 번역이 있는지 확인
        const existingTranslation = chiefComplaintsTranslations.find(t => 
          t.target === cc && t.target_locale === locale
        );
        
        if (!existingTranslation) {
          translateUserStrings([cc], [locale]);
        }
      }
    }
  }, [line.label, reportState?.systemValueMap?.cc, reportState?.locale, chiefComplaintsTranslations, translateUserStrings]);

  // treeStates의 answerMap을 읽어서 배열 생성
  const treeStatesInfo: string[] = [];

  // visit에서 treeStates 데이터를 가져옵니다
  const treeStates = reportState?.treeStates || [];
  
  treeStates.forEach((treeState: any) => {
    const { treeKey: stateTreeKey, answerMap } = treeState;
    
    if (answerMap && Object.keys(answerMap).length > 0) {
      Object.entries(answerMap).forEach(([key, value]: [string, any]) => {
        if (value.answerValues && !stateTreeKey.includes("sx-") && !stateTreeKey.includes("chief-complaint")) {
          if (value.answerValues.length > 0) {
            treeStatesInfo.push(`${stateTreeKey}, ${key.replace("q-", "")}: ${value.answerValues.join(',')}`);
          } else {
            treeStatesInfo.push(`${stateTreeKey}, ${key.replace("q-", "")}: None`);
          }
        }
      });
    }
  });

  const forceVisible = !isNil(line.visibility?.visible?.find(i => i === jobFunction));
  const forceHidden = !isNil(line.visibility?.hidden?.find(i => i === jobFunction));
  const forceCollapsed = !isNil(line.visibility?.collapsed?.find(i => i === jobFunction));
  
  if (forceHidden || (line.visibility?.default === 'Hidden' && !forceVisible && !forceCollapsed)) {
    return <></>
  }

  const collapsed = forceCollapsed || (line.visibility?.default === 'Collapsed' && !forceVisible && !forceHidden);

  const comments = adHocData?.adHocComments?.filter(comment => comment.deleted !== true && comment.template === line.id &&
    (comment.visitId === line.visitId || (!isAddOnForm && isEmpty(line.visitId))));

return (
    <Accordion defaultExpanded={!collapsed || false}
      elevation={0}
      variant="outlined"
      sx={{
        borderColor: '#d1d5db',
        borderRadius: '1.5px',
        margin: '0 0 0 0',
        '&.MuiAccordion-root:first-of-type':
        {
          borderColor: '#d1d5db',
          borderRadius: '1.5px',
        },
        '&.MuiAccordion-root.Mui-expanded':
        {
          margin: '0 0 0 0',
        },
        '&.MuiAccordion-root:last-of-type':
        {
          borderColor: '#d1d5db',
          borderRadius: '1.5px',
        },
        '&:before': { display: 'none' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${keyId}-content`}
        id={`${keyId}-header`}
        sx={{
          padding: '0 0.5rem',
          margin: '0.25rem 0',
          minHeight: '0.25rem',
          '.MuiAccordionSummary-content.Mui-expanded': {
            margin: '0px 0px',
            minHeight: '0.25rem',
          },
          '.MuiAccordionSummary-content': {
            margin: '0px 0px',
            minHeight: '0.25rem',
          },
          '&.Mui-expanded': {
            margin: '0.25rem 0rem 0rem 0rem',
            minHeight: '0.25rem',
          }
        }}
      >
        <div className='card-body p-1 gap-0 flex-row justify-between'>
          <div className='flex-center'>
            <H5 style={{fontSize: "22"}}>
              <div className='flex items-center'>
                {line.label}
                {isAddOnForm &&
                  <FaPaperPlane className="ml-1" size={17} />
                }
              </div>
            </H5>
          </div>

          <div className='flex-row'>
            <div className='flex flex-end relative items-center'>

              <span className='px-2 cursor-pointer h-fit w-fit'
                role="button"
                title={sltStr({ key: 'report-card-add-comment' })}
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenModalAddComment(true);
                }}>
                <FaProCommentMedical width={17} />
              </span>

{demoSignature && (
                <span className='p-1 cursor-pointer h-fit w-fit'
                  role="button"
                  title={sltStr({ key: 'report-card-download-pdf' })}
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();

                    // 널 체크 추가: downloadableForm.current가 존재하는지 확인합니다.
                    const element = downloadableForm.current;
                    if (!element) {
                      console.warn("downloadableForm.current is null. Cannot generate PDF.");
                      // 선택 사항: 사용자에게 PDF 생성에 실패했음을 알리는 토스트 메시지 등을 추가할 수 있습니다.
                      return; // 함수 실행을 중단합니다.
                    }

                    const filename = `${visit?.firstName || visit?.id} ${visit?.lastName || visit?.id} ${treeKey}.pdf`

                    // TODO: [CRITICAL] - XSS vulnerability through innerHTML in PDF generation
                    // ISSUE: Using element.innerHTML in PDF generation creates XSS risk if content contains user input
                    // RECOMMENDATION: Sanitize HTML content or use safer DOM methods before PDF conversion
                    html2pdf()
                      // @ts-ignore // element가 null이 아님을 확인했으므로 이 @ts-ignore는 유지해도 됩니다.
                      .from(element.innerHTML) // 이제 element는 null이 아님이 보장됩니다.
                      .set({
                        html2canvas: {
                          width: 1024,
                          scale: 1,
                        },
                        filename,
                        jsPDF: { format: 'a4' },
                        margin: [10, 10, 10, 10],
                        pagebreak: { avoid: 'img' }
                      })
                      .save();

                  }}>
                  <FaArrowCircleDown size={17} />
                </span>
              )}
              {isElectron && (
                <span
                  className='p-1 cursor-pointer h-fit w-fit'
                  onClick={(event) => {
                    event.stopPropagation();

                    let outputStr = '';
                    if (isPhiLine) {
                      outputStr = getStringFromPhis({ line, phis: phis || [], tNote, locale });
                    } else {
                      outputStr = getStringFromLine(line, tNote, locale);
                    }

                    if (outputStr) {
                      setIsSendingData(true);
                      window.electron?.ipcRenderer?.invokeMessage('send', [`${outputStr}\n`]).finally(() => setIsSendingData(false));
                    }
                  }}
                >
                  {isSendingData ? <span className='loading loading-xs loading-spinner'></span> : <FaShareSquare size={17} />}
                </span>
              )}

              <span
                className='p-1 cursor-pointer h-fit w-fit'
                onClick={(event) => {
                  event.stopPropagation();

                  if (markUserActive) markUserActive();

                  let outputStr = '';
                  if (isPhiLine) {
                    outputStr = getStringFromPhis({ line, phis: phis || [], tNote, locale });
                  } else {
                    outputStr = getStringFromLine(line, tNote, locale);
                  }

                  if (outputStr) {
                    copy(`${outputStr}\n`);
                    showCopiedMsg();
                  } else {
                    showNothingToCopyMsg();
                  }
                }}
              >
                <IoMdCopy size={17} />
              </span>
              <span className='p-2 cursor-pointer h-fit w-fit hidden'>
                <BiPencil size={17} />
              </span>
            </div>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails
        id={`${keyId}-content`}
        sx={{
          margin: '0px 0px',
          padding: '0rem 0.5rem 0.25rem 0.5rem'
        }}
      >
        <div className='card-body p-4 md:p-1 gap-0'>
          {/* HPI 내용은 항상 원래 형식으로 표시 (Descriptive Report는 이제 이 아래에서 별도로 표시) */}
          <div>
            {(() => {
              let phisRendered = false; // PHI가 이미 렌더링되었는지 추적
              return line.items.map((item) => {
                  const { id: itemId, type, reportConfig } = item;
                if (type === 'rep-report-template') {
                  if (line.label === "CC") {
                    if (!isDemo) {
                      const ccOrig = reportState?.systemValueMap?.cc as string;
                      
                      // 번역된 텍스트 찾기
                      let ccTrans = chiefComplaintsTranslations.find(t => 
                        t.target === ccOrig && t.target_locale === locale
                      )?.output || null;
                      
                      // 번역이 원본과 같으면 무시 (영어로 작성된 경우)
                      if (ccTrans === ccOrig) {
                        ccTrans = ccOrig;
                      }
                      
                       return (
                         <div key={itemId}>
                           <div className="text-black mt-1" style={{ color: '#000000' }}>
                             {renderReportText(ccTrans || ccOrig)}
                           </div>
                           
                           {ccTrans && ccTrans !== ccOrig && (() => {
                             const languageName = getLanguageName(locale);
                             return (
                               <div className="text-xs text-gray-400 mt-1">
                                 [{reportAutoTranslatedLabel} {languageName} <span className="italic">{ccOrig}</span>]
                               </div>
                             );
                           })()}
                         </div>
                       );
                    }
                  }

                  if (line.label === "Allergy Hx") {
                    // treeStates에서 allergy 데이터를 nested 형식으로 가져오기
                    const allergyTreeState = reportState?.treeStates?.find((ts: any) => 
                      ts.treeKey?.includes('allergy') || 
                      ts.answerMap?.['q-pmhx-allergy-cause-nested-athena'] || 
                      ts.answerMap?.['q-pmhx-allergy-cause-nested']
                    );
                    
                    const allergyQuestionKey = allergyTreeState?.answerMap?.['q-pmhx-allergy-cause-nested-athena'] 
                      ? 'q-pmhx-allergy-cause-nested-athena' 
                      : 'q-pmhx-allergy-cause-nested';
                    
                    const allergyAnswer = allergyTreeState?.answerMap?.[allergyQuestionKey];
                    const allergyAnswerValues = allergyAnswer?.answerValues || [];
                    const allergyAnswerMaps = allergyAnswer?.answerMaps || [];
                    
                    // answerValues가 문자열인 경우 (|로 구분) 처리
                    const allergyNames = allergyAnswerValues.length > 0 && typeof allergyAnswerValues[0] === 'string' && allergyAnswerValues[0].includes('|')
                      ? allergyAnswerValues[0].split('|').filter((name: string) => name.trim())
                      : allergyAnswerValues.filter((name: any) => name && typeof name === 'string' && name.trim());
                    
                    // nested 형식으로 allergy 데이터 구성
                    const nestedAllergyData = allergyNames.map((allergyName: string, index: number) => {
                      const answerMap = allergyAnswerMaps[index];
                      const reactions = (answerMap?.answerKeys as string[]) || [];
                      const reactionLabels = (answerMap?.answerLabels as string[]) || [];
                      
                      // reaction 키를 텍스트로 변환
                      // answerLabels가 있으면 사용, 없으면 nested-0-11 형식에서 질문 정보로 찾기
                      const reactionTexts = Array.isArray(reactions) ? reactions.map((reactionKey: string, reactionIdx: number) => {
                        // answerLabels가 있으면 사용
                        if (reactionLabels && reactionLabels.length > reactionIdx && reactionLabels[reactionIdx]) {
                          return reactionLabels[reactionIdx];
                        }
                        
                        // nested-0-11 형식 처리
                        if (reactionKey.startsWith('nested-')) {
                          const parts = reactionKey.split('-');
                          if (parts.length >= 3) {
                            const questionIndex = parseInt(parts[1], 10);
                            const optionIndex = parseInt(parts[2], 10);
                            
                            // 질문 정보 가져오기
                            const question = getQuestionByKey(allergyQuestionKey);
                            if (question?.answerOptionsMap) {
                              // answerOptionsMap을 nestedQuestions 구조로 변환
                              const optionsByQuestion: Record<string, Array<{key: string, label: string}>> = {};
                              
                              Object.entries(question.answerOptionsMap as Record<string, any>).forEach(([key, option]) => {
                                const questionText = option?.question || 'Nested Question 1';
                                if (!optionsByQuestion[questionText]) {
                                  optionsByQuestion[questionText] = [];
                                }
                                optionsByQuestion[questionText].push({
                                  key,
                                  label: option?.label || key,
                                });
                              });
                              
                              // 각 질문별로 정렬 (order 필드 사용)
                              Object.keys(optionsByQuestion).forEach(questionText => {
                                optionsByQuestion[questionText].sort((a, b) => {
                                  const aOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.label === a.label);
                                  const bOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.label === b.label);
                                  const aOrder = aOption?.order || 0;
                                  const bOrder = bOption?.order || 0;
                                  return aOrder - bOrder;
                                });
                              });
                              
                              // 질문들을 questionIndex 순서로 정렬
                              const sortedQuestions = Object.entries(optionsByQuestion).sort((a, b) => {
                                const aFirstOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.question === a[0]);
                                const bFirstOption = Object.values(question.answerOptionsMap as Record<string, any>).find((opt: any) => opt?.question === b[0]);
                                const aOrder = aFirstOption?.questionOrder || 0;
                                const bOrder = bFirstOption?.questionOrder || 0;
                                return aOrder - bOrder;
                              });
                              
                              // questionIndex에 해당하는 질문 찾기
                              const nestedQuestion = sortedQuestions[questionIndex];
                              if (nestedQuestion && nestedQuestion[1]) {
                                const options = nestedQuestion[1];
                                const option = options[optionIndex];
                                if (option?.label) {
                                  return td?.(option.label) || option.label;
                                }
                              }
                            }
                          }
                        }
                        
                        // 기본 처리: td 함수 사용 또는 키 변환
                        const reactionText = td?.(reactionKey) || reactionKey.replace(/^sx-|^o-/, '').replace(/-/g, ' ');
                        return reactionText;
                      }) : [];
                      
                      return {
                        allergy: allergyName.trim(),
                        reactions: reactionTexts
                      };
                    }).filter((allergyItem: any) => allergyItem.allergy && allergyItem.allergy.trim());
                    
                    // nested 형식으로 렌더링
                    const allergyDisplay = nestedAllergyData.length > 0 ? (
                      <div className="py-1">
                        {nestedAllergyData.map((allergyItem: any, idx: number) => (
                          <div key={`allergy-${idx}`} className="py-1">
                            <div className="font-medium">
                              - {renderReportText(allergyItem.allergy)}
                            </div>
                            {allergyItem.reactions && allergyItem.reactions.length > 0 && (
                              <div className="ml-6 py-0.5">
                                - {allergyItem.reactions.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      // fallback: 기존 방식으로 표시
                      reportConfig?.lines.map((reportLine, lineIdx) => {
                        if (reportLine.output && typeof reportLine.output === 'string') {
                          return (
                            <div key={`${lineIdx}-${reportLine.id}`}>
                              <div className="flex-row gap-2 py-0.5">
                                <span className="flex-center">
                                  {renderReportText(reportLine.output)}
                                </span>
                                {demoSignature && lineIdx === 0 &&
                                  <>
                                    <span className="flex-center" style={{ maxWidth: '5em' }}>
                                      <img src={demoSignature} alt={sltStr({ key: 'report-card-signature-alt' })} />
                                    </span>
                                  </>}
                              </div>
                              {reportLine.sublines.map((subline, sublineIdx) => {
                                return (
                                  <div key={`subline-${sublineIdx}`} className="py-0.5">
                                    {renderReportText(subline.output)}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }
                        return null;
                      }).filter(Boolean)
                    );

                    return (
                      <div key={itemId}>
                        <div>{item.label}</div>
                        {allergyDisplay}
                      </div>
                    );
                  }
                  
                  // 일반적인 경우 처리
                  return (
                    <div key={itemId}>
                      <div>{item.label}</div>
                      {reportConfig?.lines.map((reportLine, lineIdx) => {
                        return (
                          <div key={`${lineIdx}-${reportLine.id}`}>
                            <div className="flex-row gap-2 py-0.5">
                              <span className="flex-center">
                                {renderReportText(reportLine.output)}
                              </span>
                              {demoSignature && lineIdx === 0 &&
                                <>
                                  <span className="flex-center" style={{ maxWidth: '5em' }}>
                                    <img src={demoSignature} alt={sltStr({ key: 'report-card-signature-alt' })} />
                                  </span>
                                </>}
                            </div>
                            {reportLine.sublines.map((subline, sublineIdx) => {
                              return (
                                <div key={`subline-${sublineIdx}`} className="py-0.5">
                                  {renderReportText(subline.output)}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                if (phis && type === 'rep-phi-list') {
                  // PHI가 이미 렌더링되었으면 스킵 (같은 카드 내 중복 방지)
                  if (phisRendered) {
                    return null;
                  }
                  phisRendered = true;

                  // 중복 제거: 같은 treeKey와 lineOutput을 가진 항목은 하나만 표시
                  const uniquePhis = uniqWith(phis, (a, b) =>
                    a.treeKey === b.treeKey && a.lineOutput === b.lineOutput
                  );

                  return (
                    <div key={itemId}>
                      {uniquePhis.map((phi, phiIdx, array) => {
                        const onsetDate = formatDTsToReportDateFormat(phi.onsetDate);
                        const lastOnsetDate = phiIdx > 0 ? formatDTsToReportDateFormat(array[phiIdx - 1].onsetDate) : '';
                        const showOnsetDate = onsetDate !== lastOnsetDate;

                        return (
                          <div className={classNames(phiIdx && "pt-2")} key={`${phiIdx} - ${phi.treeKey}-${phi.onsetDate}`}>
                            {showOnsetDate && <div className="text-gray-500">{onsetDate} <span className="ml-2 italic text-sm">{phi.relativeOnset} </span></div>}
                            <div className='pl-10 py-1'>{renderReportText(phi.lineOutput)}</div>
                            {phi.sublinesOutput.map((sublineOutput, sublineIdx) => {
                              return (
                                <div className='pl-10 py-0.5 text-gray' key={`subline-${sublineIdx}`}>
                                  {renderReportText(sublineOutput)}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                const itemOutput = consolidateFlowItem({
                  item,
                });

                return itemOutput;
              })
              .filter(notNull);
            })()}
          </div>
        </div>
      </AccordionDetails>

      {/* GENERATE DESCRIPTIVE NOTE 버튼은 HPI 섹션일 때만 표시 */}
      {isHpiLine && (
        // 버튼을 감싸는 div의 클래스 수정:
        // - `justify-start` 대신 `px-4` (혹은 다른 적절한 패딩 값)으로 왼쪽 정렬
        // - `w-full`로 너비를 꽉 채움.
        <div className="flex justify-center w-full pt-2 pb-2"> {/* <-- flex justify-center w-full 유지, px-4로 좌우 패딩만 조절, py-2로 상하 간격 조절 */}
          <GenerateDescriptiveNoteButton
            reportState={reportState}
            visit={visit}
            onReportGenerated={onReportGenerated}
            setIsGeneratingDesc={setIsGeneratingDesc}
            promptInfoHpi={promptInfoHpi}
            treeStatesInfo={treeStatesInfo}
            isDemo={isDemo}
          />
        </div>
      )}

{isHpiLine && reportState?.descriptiveReport && (
        <div className="pt-0 pb-4 px-10">
          {/* 복사 아이콘을 텍스트 박스 바로 위 (혹은 내부에) 배치 */}
          {/* 기존 flex justify-end mb-1 div는 삭제하거나 내용을 비웁니다. */}
          {/* <div className="flex justify-end mb-1">
            <span
              className="p-1 cursor-pointer h-fit w-fit"
              onClick={() => {
                copy(reportState.descriptiveReport!);
                showCopiedMsg();
              }}
            >
              <IoMdCopy size={17} />
            </span>
          </div> */}

          {/* 텍스트 박스에 relative 포지션을 주고, 아이콘은 absolute 포지션으로 내부 배치 */}
          <div className="whitespace-pre-wrap rounded-md bg-white p-8 border relative"> {/* <-- relative 추가 */}
            {reportState?.descriptiveReport}
            
            {/* 복사 아이콘을 이 div 내부에 absolute로 배치 */}
            <span
              className="absolute top-2 right-2 p-1 cursor-pointer" // <-- absolute, top-2, right-2, p-1 (Tailwind)
              onClick={() => {
                if (reportState?.descriptiveReport) {
                  copy(reportState.descriptiveReport);
                  showCopiedMsg();
                }
              }}
            >
              <IoMdCopy size={17} />
            </span>
          </div>
        </div>
      )}

      {openModalAddComment &&
                  <ModalAddComment
                    existingComment={editComment}
                    onClose={() => { setOpenModalAddComment(false); setEditComment(null); }}
                    onSave={(comment: string) => {
                      updateAdhocComment({
                        variables: {
                          id: visit?.id || "0",
                          data: {
                            adHocState: {
                              adHocComments: [{
                                visitId: line.visitId || visit?.id || "",
                                template: line.id,
                                userId: user?.id || "",
                                userName: user?.displayName || "",
                                commentText: comment,
                                uniqueId: genMessageLongId(),
                                timestamp: formatDate(new Date(), { formatStr: SystemDateTimeSec }) // SystemDateTimeSec
                              }]
                            }
                          }
                        },
                      }).then(() => {
                        setOpenModalAddComment(false);
                        setEditComment(null);
                      });
                    }}
                    onEdit={(editedComment: AdhocCommentType) => {
                      updateAdhocComment({
                        variables: {
                          id: visit?.id || "0",
                          data: {
                            adHocState: {
                              adHocComments: [
                                editedComment
                              ]
                            }
                          }
                        },
                      }).then(() => {
                        setOpenModalAddComment(false);
                        setEditComment(null);
                      });
                    }}
                  />}
                {(demoSignature && treeKey) && (
                  <div ref={downloadableForm} style={{ display: "none" }}>
                    <PaTreePure
                      treeKey={treeKey}
                      preloadNext={false}
                      mode="pdf"
                      locale={Enum_Translation_Locale.English}
                      noHeader
                    />
                  </div>
                )}

</Accordion>
  );
}

export default memo(ReportCard);
